import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useImageZoom } from "../hooks/useImageZoom";
import { ZoomControls } from "./ZoomControls";
import { ZoomableImageModal } from "./ZoomableImageModal";

export type CompareMode = "sidebyside" | "diff" | "overlay";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: #000;
  display: flex;
  flex-direction: column;
  z-index: 2000;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #1c1c1c;
  color: #fff;
  gap: 12px;
`;

const ModeButtons = styled.div`
  display: flex;
  gap: 4px;
`;

const ModeButton = styled.button<{ active?: boolean }>`
  background: ${(p) => (p.active ? "#424242" : "#2d2d2d")};
  color: ${(p) => (p.active ? "#fff" : "#bdbdbd")};
  border: 1px solid #424242;
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const CloseBtn = styled.button`
  background: none;
  color: #fff;
  border: none;
  font-size: 24px;
  cursor: pointer;
`;

const Body = styled.div`
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px 20px 100px 20px;
`;

const SideBySideGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
`;

const ImgCard = styled.div`
  background: #2d2d2d;
  border-radius: 4px;
  overflow: hidden;
  h4 {
    padding: 8px 12px;
    margin: 0;
    color: #bdbdbd;
    font-size: 12px;
    background: #1c1c1c;
  }
`;

// shared zoom (useImageZoom) を全モードの画像に適用するための transform。
// transition は drag 中だけ無効化（追従感のため）。
const ZoomableImg = styled.img<{
  scale: number;
  position: { x: number; y: number };
  isDragging: boolean;
}>`
  width: 100%;
  display: block;
  transform: scale(${(p) => p.scale}) translate(${(p) => p.position.x}px, ${(p) => p.position.y}px);
  transform-origin: center top;
  transition: ${(p) => (p.isDragging ? "none" : "transform 0.2s")};
  cursor: ${(p) => (p.isDragging ? "grabbing" : "grab")};
  user-select: none;
  -webkit-user-drag: none;
`;

const DiffWrap = styled.div`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
`;

const DiffImg = styled.img<{
  scale: number;
  position: { x: number; y: number };
  isDragging: boolean;
}>`
  max-width: 95vw;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
  transform: scale(${(p) => p.scale}) translate(${(p) => p.position.x}px, ${(p) => p.position.y}px);
  transition: ${(p) => (p.isDragging ? "none" : "transform 0.2s")};
  cursor: ${(p) => (p.isDragging ? "grabbing" : "grab")};
  user-select: none;
  -webkit-user-drag: none;
`;

const OverlayWrap = styled.div`
  position: relative;
  display: inline-block;
  max-width: 100%;
  user-select: none;
  -webkit-user-select: none;
`;

// 重ね合わせの 2 枚を同位置に重ね、表示する側だけを visible にして flicker 比較する。
// current を非絶対配置にして wrap の box サイズを current の自然サイズで決める。
// 両画像とも D&D で移動可能（#202）。シングルクリックでは Baseline/Current が
// 入れ替わる（tfact_user_request#215）。D&D 終端の mouseup→click 誤切替は OverlayWrap 側の
// dragDistance チェックで抑止する。
// cursor は「クリック可能」を伝えるため pointer。ドラッグ中は grabbing。
const StackImg = styled.img<{
  visible: boolean;
  scale: number;
  position: { x: number; y: number };
  isDragging: boolean;
}>`
  display: block;
  max-width: 100%;
  visibility: ${(p) => (p.visible ? "visible" : "hidden")};
  transform: scale(${(p) => p.scale}) translate(${(p) => p.position.x}px, ${(p) => p.position.y}px);
  transition: ${(p) => (p.isDragging ? "none" : "transform 0.2s")};
  cursor: ${(p) => (p.isDragging ? "grabbing" : "pointer")};
  user-select: none;
  -webkit-user-drag: none;
`;

const StackImgAbsolute = styled.img<{
  visible: boolean;
  scale: number;
  position: { x: number; y: number };
  isDragging: boolean;
}>`
  position: absolute;
  inset: 0;
  max-width: 100%;
  display: block;
  visibility: ${(p) => (p.visible ? "visible" : "hidden")};
  transform: scale(${(p) => p.scale}) translate(${(p) => p.position.x}px, ${(p) => p.position.y}px);
  transition: ${(p) => (p.isDragging ? "none" : "transform 0.2s")};
  cursor: ${(p) => (p.isDragging ? "grabbing" : "pointer")};
  user-select: none;
  -webkit-user-drag: none;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 20px;
  background: #1c1c1c;
  color: #bdbdbd;
  font-size: 12px;
`;

const ToggleButton = styled.button<{ active?: boolean }>`
  background: ${(p) => (p.active ? "#00796b" : "#2d2d2d")};
  color: ${(p) => (p.active ? "#fff" : "#bdbdbd")};
  border: 1px solid ${(p) => (p.active ? "#00796b" : "#424242")};
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
`;

export interface ImageCompareModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: CompareMode;
  onModeChange: (m: CompareMode) => void;
  baseline?: string;
  current?: string;
  diff?: string;
}

export const ImageCompareModal: React.FC<ImageCompareModalProps> = ({
  isOpen,
  onClose,
  mode,
  onModeChange,
  baseline,
  current,
  diff,
}) => {
  const [overlaySide, setOverlaySide] = useState<"baseline" | "current">("baseline");

  // baseline と current が両方揃って初めて比較 UI（sidebyside / 切替）が成立する。
  // 揃わないケース（リグレッション 1 回目実行など）は単体画像フォールバック（issue #192）。
  const canCompare = !!(baseline && current);
  // 比較できないときに表示する 1 枚。current > baseline > diff の優先順で拾う。
  const singleImage: string | undefined = !canCompare
    ? current ?? baseline ?? diff
    : undefined;

  // 全モードで shared zoom（issue #195 のフォロー）。
  // shared にすることで sidebyside / 切替モードで baseline と current を
  // 同じ scale / position で揃えて比較できる。
  // hooks は早期 return より前に呼ぶ必要があるため、上で常時呼び出す。
  const zoom = useImageZoom({
    enabled: isOpen && (canCompare || !!singleImage),
    onEscape: onClose,
  });

  if (!isOpen) return null;

  // 表示する画像が一切無いケースはモーダルを開かない（背景だけ真っ黒問題の最後の砦）。
  if (!canCompare && !singleImage) return null;

  // 単体画像時（first-run 等）は ZoomableImageModal に委譲（モードタブ不要なため最小構成）。
  if (!canCompare && singleImage) {
    return <ZoomableImageModal isOpen={isOpen} imageUrl={singleImage} onClose={onClose} />;
  }

  // 比較モード（sidebyside / diff / overlay）は ModeButtons + Body + ZoomControls の統合 UI。
  // 全モードで shared zoom を効かせ、モード切替時にも zoom 状態を維持する。
  return createPortal(
    <Overlay data-testid="image-compare-overlay" onClick={onClose}>
      <Toolbar onClick={(e) => e.stopPropagation()}>
        <ModeButtons>
          <ModeButton
            active={mode === "sidebyside"}
            disabled={!baseline || !current}
            onClick={() => onModeChange("sidebyside")}
          >
            Side by Side
          </ModeButton>
          <ModeButton
            active={mode === "diff"}
            disabled={!diff}
            onClick={() => onModeChange("diff")}
          >
            Diff
          </ModeButton>
          <ModeButton
            active={mode === "overlay"}
            disabled={!baseline || !current}
            onClick={() => onModeChange("overlay")}
          >
            切替
          </ModeButton>
        </ModeButtons>
        <CloseBtn onClick={onClose} aria-label="閉じる">×</CloseBtn>
      </Toolbar>
      {mode === "overlay" && (
        <ToggleRow onClick={(e) => e.stopPropagation()}>
          <span>表示:</span>
          <ToggleButton
            active={overlaySide === "baseline"}
            onClick={() => setOverlaySide("baseline")}
          >Baseline</ToggleButton>
          <ToggleButton
            active={overlaySide === "current"}
            onClick={() => setOverlaySide("current")}
          >Current</ToggleButton>
        </ToggleRow>
      )}
      <Body onClick={(e) => e.stopPropagation()}>
        {mode === "sidebyside" && baseline && current && (
          <SideBySideGrid>
            <ImgCard>
              <h4>Baseline</h4>
              <ZoomableImg
                src={baseline}
                alt="baseline"
                scale={zoom.scale}
                position={zoom.position}
                isDragging={zoom.isDragging}
                onMouseDown={zoom.handleMouseDown}
              />
            </ImgCard>
            <ImgCard>
              <h4>Current</h4>
              <ZoomableImg
                src={current}
                alt="current"
                scale={zoom.scale}
                position={zoom.position}
                isDragging={zoom.isDragging}
                onMouseDown={zoom.handleMouseDown}
              />
            </ImgCard>
          </SideBySideGrid>
        )}
        {mode === "diff" && diff && (
          <DiffWrap>
            <DiffImg
              src={diff}
              alt="diff"
              scale={zoom.scale}
              position={zoom.position}
              isDragging={zoom.isDragging}
              onMouseDown={zoom.handleMouseDown}
            />
          </DiffWrap>
        )}
        {mode === "overlay" && baseline && current && (
          // 切替モード: tfact_user_request#215 で「画像クリックで Baseline/Current 切替」を復活。
          // D&D 完了時の mouseup→click による誤切替は OverlayWrap の onClick 内で
          // zoom.dragDistance（直近 mousedown 以降の最大移動距離）を見て弾く（#203 由来の課題対策）。
          // 両画像とも onMouseDown を維持して D&D 移動を有効化する（#202）。
          // tfact_user_request#217: 素早い 2 連クリックで dblclick が発火し意図せず拡大/リセット
          // されていたため、切替モードではダブルクリック拡大を無効化（ZoomControls の
          // ヒントも当初から「クリック: 切替」のみで dblclick は案内していない）。
          // 拡大はホイールまたは下部ボタンで操作する。
          <OverlayWrap
            onClick={() => {
              if (zoom.dragDistance > 5) return;
              setOverlaySide((s) => (s === "baseline" ? "current" : "baseline"));
            }}
            title="クリックで Baseline/Current を切替"
          >
            <StackImg
              src={current}
              alt="current"
              visible={overlaySide === "current"}
              scale={zoom.scale}
              position={zoom.position}
              isDragging={zoom.isDragging}
              onMouseDown={zoom.handleMouseDown}
            />
            <StackImgAbsolute
              src={baseline}
              alt="baseline"
              visible={overlaySide === "baseline"}
              scale={zoom.scale}
              position={zoom.position}
              isDragging={zoom.isDragging}
              onMouseDown={zoom.handleMouseDown}
            />
          </OverlayWrap>
        )}
      </Body>
      <ZoomControls
        scale={zoom.scale}
        position={zoom.position}
        onZoomIn={zoom.zoomIn}
        onZoomOut={zoom.zoomOut}
        onResetZoom={zoom.resetZoom}
        onResetPosition={zoom.resetPosition}
        clickAction={mode === "overlay" ? "switch" : "none"}
      />
    </Overlay>,
    document.body
  );
};
